import React from 'react';
import { BackgroundTaskContext } from './BackgroundTaskProvider';

/**
 * Hook that registers a callback to fire when a background task completes.
 *
 * Callbacks are fire-once: they execute when the task reaches a terminal
 * state (completed/failed/cancelled) and are then removed automatically.
 * They are NOT tied to component lifecycle — if the registering component
 * unmounts before the task completes, the callback still fires. This is
 * intentional: the callback typically calls a stable setter (like
 * setLastUpdate) from a parent component that remains mounted.
 *
 * Usage:
 *   const onTaskDone = useTaskCallback();
 *
 *   if (isBackgroundTaskResponse(res)) {
 *       notifyBackgroundTask(res, enqueueSnackbar, closeSnackbar, bgCtx);
 *       onTaskDone(res.data.task_id, (task) => {
 *           // task has: id, state, state_display, task_type, task_label, error_message, ...
 *           if (task.state === 2) {  // COMPLETED
 *               setLastUpdate(Date.now());  // refresh grid
 *           }
 *       });
 *   }
 */
const useTaskCallback = () => {
    const { registerTaskCallback } = React.useContext(BackgroundTaskContext);

    return React.useCallback((taskId, callback) => {
        registerTaskCallback(taskId, callback);
    }, [registerTaskCallback]);
};

export default useTaskCallback;
